import React, { useState, useEffect, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalUser } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.turingscout.com';

// ── Subtle grid background ───────────────────────────────────────────────────
const GridBg: React.FC = () => (
  <svg style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 } as CSSProperties} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="oklch(88% 0.012 260)" strokeWidth="0.5" />
      </pattern>
      <radialGradient id="fade" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="oklch(98.5% 0.005 260)" stopOpacity="0" />
        <stop offset="100%" stopColor="oklch(98.5% 0.005 260)" stopOpacity="1" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid)" />
    <rect width="100%" height="100%" fill="url(#fade)" />
  </svg>
);

// ── Icon ─────────────────────────────────────────────────────────────────────
interface IconProps { name: string; size?: number; color?: string; }
const Icon: React.FC<IconProps> = ({ name, size = 16, color = "currentColor" }) => {
  const icons: Record<string, React.ReactNode> = {
    code: <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    eyeOff: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>,
    mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    check: <><polyline points="20 6 9 12 4 9"/></>,
    alert: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>,
    spinner: <><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

// ── Spinning loader ───────────────────────────────────────────────────────────
const Spinner: React.FC = () => {
  const [deg, setDeg] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDeg(d => d + 30), 80);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ display: "inline-flex" }}>
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ transform: `rotate(${deg}deg)` }}>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeOpacity="0.3"/>
        <path d="M12 2v4" stroke="currentColor" strokeOpacity="1"/>
      </svg>
    </div>
  );
};

// ── Input field ───────────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  icon?: string;
  error?: string;
  autoFocus?: boolean;
  suffix?: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, type = "text", value, onChange, placeholder, icon, error, autoFocus, suffix }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>{label}</label>
      <div style={{ position: "relative" }}>
        {icon && (
          <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: focused ? "var(--accent)" : "var(--text-3)", transition: "color 0.15s" }}>
            <Icon name={icon} size={15} color={focused ? "var(--accent)" : error ? "var(--red)" : "var(--text-3)"} />
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: `10px ${suffix ? "42px" : "12px"} 10px ${icon ? "36px" : "12px"}`,
            border: `1.5px solid ${error ? "var(--red)" : focused ? "var(--accent)" : "var(--border)"}`,
            borderRadius: "var(--radius-sm)",
            fontSize: 13.5,
            background: focused ? "#fff" : "var(--bg)",
            outline: "none",
            transition: "border-color 0.15s, background 0.15s",
            color: "var(--text)",
            fontFamily: "inherit",
          }}
        />
        {suffix && (
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", paddingRight: 4 }}>
            {suffix}
          </div>
        )}
      </div>
      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "var(--red)" }}>
          <Icon name="alert" size={12} color="var(--red)" />
          {error}
        </div>
      )}
    </div>
  );
};

// ── Sign In View ──────────────────────────────────────────────────────────────
interface SignInViewProps { onSignIn: (user: PortalUser, token: string) => void; onForgot: () => void; }
const SignInView: React.FC<SignInViewProps> = ({ onSignIn, onForgot }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState<{ email?: string; password?: string }>({});

  const validate = () => {
    const errs: { email?: string; password?: string } = {};
    if (!email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldError(errs); return; }
    setFieldError({});
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (res.status === 401 || res.status === 403) {
        setError("Incorrect email or password.");
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      onSignIn(data.user as PortalUser, data.token as string);
    } catch (err: any) {
      setError(err.message === "Failed to fetch" ? "Unable to reach server. Please try again." : err.message);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Field label="Email address" icon="mail" type="email" value={email}
        onChange={e => { setEmail(e.target.value); setFieldError(f => ({ ...f, email: "" })); }}
        placeholder="you@company.com" autoFocus error={fieldError.email} />

      <Field label="Password" icon="lock" type={showPass ? "text" : "password"} value={password}
        onChange={e => { setPassword(e.target.value); setFieldError(f => ({ ...f, password: "" })); }}
        placeholder="••••••••" error={fieldError.password}
        suffix={
          <button type="button" onClick={() => setShowPass(s => !s)}
            style={{ padding: "6px 8px", borderRadius: 4, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer" }}>
            <Icon name={showPass ? "eyeOff" : "eye"} size={15} />
          </button>
        }
      />

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -8 }}>
        <button type="button" onClick={onForgot}
          style={{ fontSize: 12.5, color: "var(--accent)", background: "none", border: "none", cursor: "pointer", fontWeight: 500, fontFamily: "inherit" }}>
          Forgot password?
        </button>
      </div>

      {error && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", background: "var(--red-light)", border: "1px solid oklch(88% 0.08 25)", borderRadius: "var(--radius-sm)", fontSize: 12.5, color: "var(--red)" }}>
          <Icon name="alert" size={14} color="var(--red)" />
          <span>{error}</span>
        </div>
      )}

      <button type="submit" disabled={loading}
        style={{ width: "100%", padding: "11px 16px", background: loading ? "oklch(65% 0.15 265)" : "var(--accent)", color: "#fff", border: "none", borderRadius: "var(--radius-sm)", fontSize: 14, fontWeight: 600, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.15s", marginTop: 4, fontFamily: "inherit" }}>
        {loading ? <><Spinner />Signing in…</> : <>Sign in <Icon name="arrowRight" size={14} color="#fff" /></>}
      </button>
    </form>
  );
};

// ── Forgot View ───────────────────────────────────────────────────────────────
interface ForgotViewProps { onBack: () => void; }
const ForgotView: React.FC<ForgotViewProps> = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    setSent(true);
    setLoading(false);
  };

  if (sent) {
    return (
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        <div style={{ width: 52, height: 52, borderRadius: 99, background: "oklch(95% 0.05 145)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Icon name="check" size={22} color="var(--green)" />
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)", marginBottom: 8 }}>Check your inbox</div>
        <div style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 24, lineHeight: 1.6 }}>
          If <strong style={{ color: "var(--text-2)" }}>{email}</strong> is registered, you'll receive a reset link within a few minutes.
        </div>
        <button onClick={onBack} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--accent)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontSize: 13.5, color: "var(--text-3)", lineHeight: 1.6 }}>
        Enter your email and we'll send you a link to reset your password.
      </div>
      <Field label="Email address" icon="mail" type="email" value={email}
        onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoFocus />
      <button type="submit" disabled={loading || !email}
        style={{ width: "100%", padding: "11px 16px", background: !email ? "var(--bg-2)" : loading ? "oklch(65% 0.15 265)" : "var(--accent)", color: !email ? "var(--text-3)" : "#fff", border: `1px solid ${!email ? "var(--border)" : "transparent"}`, borderRadius: "var(--radius-sm)", fontSize: 14, fontWeight: 600, cursor: !email ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all 0.15s", fontFamily: "inherit" }}>
        {loading ? <><Spinner />Sending…</> : "Send reset link"}
      </button>
      <div style={{ textAlign: "center" }}>
        <button type="button" onClick={onBack} style={{ fontSize: 13, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← Back to sign in</button>
      </div>
    </form>
  );
};

// ── Success View ──────────────────────────────────────────────────────────────
interface SuccessViewProps { user: PortalUser; onContinue: () => void; }
const SuccessView: React.FC<SuccessViewProps> = ({ user, onContinue }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 50); }, []);
  const roleLabel = user.role === "superadmin" ? "Super Admin" : user.role === "admin" ? "Admin" : "Recruiter";
  const roleColor = user.role === "superadmin" ? "oklch(55% 0.15 195)" : user.role === "admin" ? "oklch(55% 0.18 295)" : "var(--accent)";
  const initials = user.name.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div style={{ textAlign: "center", padding: "12px 0", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(8px)", transition: "all 0.4s ease" }}>
      <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto 20px" }}>
        <div style={{ width: 64, height: 64, borderRadius: 99, background: roleColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#fff", letterSpacing: "-1px" }}>{initials}</div>
        <div style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22, borderRadius: 99, background: "var(--green)", border: "2.5px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="check" size={11} color="#fff" />
        </div>
      </div>
      <div style={{ fontWeight: 700, fontSize: 18, color: "var(--text)", letterSpacing: "-0.3px" }}>Welcome back, {user.name.split(" ")[0]}!</div>
      <div style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 6, marginBottom: 28 }}>
        Signed in as <strong style={{ color: "var(--text-2)" }}>{user.email}</strong>
        <span style={{ display: "inline-block", marginLeft: 8, padding: "1px 8px", borderRadius: 99, background: "var(--accent-light)", color: "var(--accent)", fontSize: 12, fontWeight: 600 }}>{roleLabel}</span>
      </div>
      <button onClick={onContinue}
        style={{ width: "100%", padding: "11px 16px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "var(--radius-sm)", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}>
        Go to Dashboard <Icon name="arrowRight" size={14} color="#fff" />
      </button>
    </div>
  );
};

// ── Login Page ────────────────────────────────────────────────────────────────
const Login: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<"signin" | "forgot" | "success">("signin");
  const [signedInUser, setSignedInUser] = useState<PortalUser | null>(null);
  const [cardVisible, setCardVisible] = useState(false);

  useEffect(() => { setTimeout(() => setCardVisible(true), 80); }, []);

  const handleSignIn = (user: PortalUser, token: string) => {
    sessionStorage.setItem("ts_portal_token", token);
    sessionStorage.setItem("ts_portal_user", JSON.stringify(user));
    setSignedInUser(user);
    setView("success");
  };

  const handleContinue = () => {
    navigate('/portal');
  };

  const viewTitle: Record<string, { heading: string | null; sub: string | null }> = {
    signin: { heading: "Sign in to TuringScout", sub: "Recruiter portal — manage candidates and assessments" },
    forgot: { heading: "Reset your password", sub: "We'll email you a secure reset link" },
    success: { heading: null, sub: null },
  };
  const currentTitle = viewTitle[view];

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      padding: 24,
      background: "var(--bg)",
      fontFamily: "'Inter', sans-serif",
      fontSize: 14,
      lineHeight: 1.5,
      color: "var(--text)",
    }}>
      <style>{`
        :root {
          --bg: oklch(98.5% 0.005 260);
          --bg-2: oklch(96% 0.008 260);
          --border: oklch(88% 0.012 260);
          --text: oklch(16% 0.015 260);
          --text-2: oklch(42% 0.02 260);
          --text-3: oklch(62% 0.015 260);
          --accent: oklch(52% 0.22 265);
          --accent-light: oklch(95% 0.06 265);
          --red: oklch(55% 0.18 25);
          --red-light: oklch(96% 0.05 25);
          --green: oklch(55% 0.15 145);
          --radius: 8px;
          --radius-sm: 5px;
        }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { height: 100%; font-family: 'Inter', sans-serif; }
      `}</style>

      <GridBg />

      {/* Card */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: 420,
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "0 4px 24px oklch(0% 0 0 / 0.08), 0 1px 4px oklch(0% 0 0 / 0.05)",
        overflow: "hidden",
        opacity: cardVisible ? 1 : 0,
        transform: cardVisible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 0.35s ease, transform 0.35s ease",
      }}>
        {/* Card top accent bar */}
        <div style={{ height: 3, background: "linear-gradient(90deg, var(--accent) 0%, oklch(60% 0.2 220) 100%)" }} />

        {/* Header */}
        <div style={{ padding: "28px 32px 24px", borderBottom: "1px solid var(--border)" }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: view !== "success" ? 24 : 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="code" size={16} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px", color: "var(--text)" }}>TuringScout</div>
              <div style={{ fontSize: 10.5, color: "var(--text-3)", fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase" }}>Recruiter Portal</div>
            </div>
          </div>
          {currentTitle.heading && (
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.4px", color: "var(--text)", lineHeight: 1.2 }}>{currentTitle.heading}</h1>
              <p style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 5 }}>{currentTitle.sub}</p>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "28px 32px 32px" }}>
          {view === "signin" && <SignInView onSignIn={handleSignIn} onForgot={() => setView("forgot")} />}
          {view === "forgot" && <ForgotView onBack={() => setView("signin")} />}
          {view === "success" && signedInUser && <SuccessView user={signedInUser} onContinue={handleContinue} />}
        </div>
      </div>

      {/* Footer */}
      <div style={{ position: "relative", zIndex: 1, marginTop: 28, fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>
        © 2026 TuringScout · <a href="/#" style={{ color: "var(--text-3)" }}>Privacy</a> · <a href="/#" style={{ color: "var(--text-3)" }}>Terms</a>
      </div>
    </div>
  );
};

export default Login;
